import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import { createKpiGroup, createKpiMonth, getKpiGroups, getKpiMe, getKpiOverview } from "../services/kpiApi";
import { currentPeriod, deltaClass, money, percent, periodLabel, shiftPeriod } from "../utils/kpiFormat";

/**
 * KPI — сводка месяца по всем таблицам КПЭ (отделы и отдельные люди). [Claude] 2026-11-02, Чат 6, №10.
 * Перенос из «Общий КПЭ»: одна группа = одна бывшая Google-таблица.
 */

const input = "min-h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-amber-400";
const btn = "min-h-9 rounded-lg px-3 py-1.5 text-sm font-semibold transition disabled:opacity-50";
const btnGhost = `${btn} border border-slate-200 bg-white text-slate-700 hover:border-slate-400`;
const btnMain = `${btn} bg-[#e0bb48] text-black hover:bg-amber-400`;

function Tiles({ s }) {
    const tiles = [
        ["Карт", s?.cards ?? 0, ""],
        ["План по КПЭ", money(s?.kpi_plan), ""],
        ["Расчёт по КПЭ", money(s?.kpi_calc), ""],
        ["Дельта", money(s?.kpi_delta), deltaClass(s?.kpi_delta)],
        ["Доп. задачи", money(s?.extra_tasks_paid), ""],
        ["К оплате", money(s?.total_pay), "text-slate-900"],
    ];
    return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
            {tiles.map(([label, value, cls]) => (
                <div key={label} className="rounded-lg bg-white border border-slate-200 px-3 py-2">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className={`text-lg font-bold ${cls || "text-slate-800"}`}>{value}</p>
                </div>
            ))}
        </div>
    );
}

function NewMonthForm({ groups, period, onDone }) {
    const { showAlert } = useAlertContext();
    const [groupId, setGroupId] = useState("");
    const [copyFrom, setCopyFrom] = useState("");
    const [busy, setBusy] = useState(false);
    const group = groups.find((g) => String(g.id) === String(groupId));
    const sources = (group?.months || []).filter((m) => m.period !== period);

    useEffect(() => { setCopyFrom(sources[0] ? String(sources[0].id) : ""); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async () => {
        if (!groupId) return;
        setBusy(true);
        try {
            const res = await createKpiMonth({ group_id: Number(groupId), period, copy_from: copyFrom ? Number(copyFrom) : null });
            showAlert?.("Месяц создан", "success");
            onDone(res?.month?.id);
        } catch (e) {
            showAlert?.(e?.message || "Не удалось создать месяц", "error");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-slate-300 bg-white p-3">
            <label className="text-xs text-slate-500">Таблица КПЭ
                <select className={`${input} block mt-1 min-w-56`} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                    <option value="">— выберите —</option>
                    {groups.filter((g) => !g.archived && !(g.months || []).some((m) => m.period === period)).map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                </select>
            </label>
            <label className="text-xs text-slate-500">Скопировать план из
                <select className={`${input} block mt-1 min-w-48`} value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} disabled={!groupId}>
                    <option value="">не копировать (пустой)</option>
                    {sources.map((m) => <option key={m.id} value={m.id}>{periodLabel(m.period)}</option>)}
                </select>
            </label>
            <button type="button" className={btnMain} disabled={!groupId || busy} onClick={submit}>
                Создать {periodLabel(period).toLowerCase()}
            </button>
            <p className="w-full text-xs text-slate-500">
                Копируются грейды, правила и карты с показателями и планом; факт, результаты и доп. задачи — пустые.
            </p>
        </div>
    );
}

export default function KpiPage() {
    const navigate = useNavigate();
    const { showAlert } = useAlertContext();
    const [params, setParams] = useSearchParams();
    const period = params.get("period") || "";
    const [data, setData] = useState(null);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openNew, setOpenNew] = useState(false);
    const [newGroup, setNewGroup] = useState("");
    const [open, setOpen] = useState({});
    const [me, setMe] = useState(null);
    const [denied, setDenied] = useState("");

    useEffect(() => { getKpiMe().then(setMe).catch(() => setMe(null)); }, []);
    const isAdmin = !!me?.is_admin;
    const canEditSome = groups.some((g) => (g.level ?? 2) >= 2);

    const setPeriod = useCallback((p) => setParams(p ? { period: p } : {}), [setParams]);

    const load = useCallback(async (p) => {
        setLoading(true);
        try {
            const [ov, gr] = await Promise.all([getKpiOverview(p), getKpiGroups()]);
            setData(ov);
            setGroups(Array.isArray(gr) ? gr : []);
            setDenied("");
            if (!period && ov?.periods?.length && !ov.groups?.length) setPeriod(ov.periods[0]);
        } catch (e) {
            if (e?.status === 403) setDenied(e?.message || "Нет доступа к KPI");
            else showAlert?.(e?.message || "Не удалось загрузить KPI", "error");
        } finally {
            setLoading(false);
        }
    }, [period, setPeriod, showAlert]);

    const effective = period || currentPeriod();
    useEffect(() => { load(effective); }, [effective]); // eslint-disable-line react-hooks/exhaustive-deps

    const periods = useMemo(() => {
        const set = new Set([...(data?.periods || []), effective, currentPeriod()]);
        return [...set].sort().reverse();
    }, [data, effective]);

    const addGroup = async () => {
        const name = newGroup.trim();
        if (!name) return;
        try {
            await createKpiGroup({ name });
            setNewGroup("");
            load(effective);
        } catch (e) {
            showAlert?.(e?.message || "Не удалось добавить", "error");
        }
    };

    return (
        <MainLayout>
            <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-bold text-slate-900 mr-2">KPI</h1>
                    <button type="button" className={btnGhost} onClick={() => setPeriod(shiftPeriod(effective, -1))} aria-label="Предыдущий месяц">←</button>
                    <select className={input} value={effective} onChange={(e) => setPeriod(e.target.value)} aria-label="Месяц">
                        {periods.map((p) => <option key={p} value={p}>{periodLabel(p)}</option>)}
                    </select>
                    <button type="button" className={btnGhost} onClick={() => setPeriod(shiftPeriod(effective, 1))} aria-label="Следующий месяц">→</button>
                    <div className="flex-1" />
                    <Link to="/company" className={btnGhost}>По подразделениям</Link>
                    {isAdmin && <Link to="/kpi/settings" className={btnGhost}>Настройки</Link>}
                    {canEditSome && (
                        <button type="button" className={btnGhost} onClick={() => setOpenNew((v) => !v)}>
                            {openNew ? "Скрыть" : "+ Месяц для таблицы"}
                        </button>
                    )}
                </div>

                {openNew && (
                    <div className="space-y-2">
                        <NewMonthForm groups={groups.filter((g) => (g.level ?? 2) >= 2)} period={effective} onDone={(id) => (id ? navigate(`/kpi/months/${id}`) : load(effective))} />
                        {isAdmin && <div className="flex flex-wrap items-center gap-2">
                            <input className={`${input} min-w-64`} placeholder="Новая таблица КПЭ (отдел или человек)" value={newGroup}
                                onChange={(e) => setNewGroup(e.target.value)} />
                            <button type="button" className={btnGhost} onClick={addGroup} disabled={!newGroup.trim()}>Добавить таблицу</button>
                        </div>}
                    </div>
                )}

                {loading && <p className="text-sm text-slate-500">Загрузка…</p>}

                {!loading && denied && (
                    <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
                        {denied}. Доступ к KPI настраивается в «KPI → Настройки → Доступ» — обратитесь к HR или владельцу платформы.
                    </p>
                )}

                {!loading && !denied && data && (
                    <>
                        <Tiles s={data.total} />
                        {!data.groups?.length && (
                            <p className="rounded-lg bg-white border border-slate-200 p-4 text-sm text-slate-500">
                                За {periodLabel(effective).toLowerCase()} карт КПЭ нет. Создайте месяц для таблицы или выберите другой месяц.
                            </p>
                        )}
                        {data.groups?.map((row) => {
                            const key = row.month.id;
                            const isOpen = open[key] !== false;
                            return (
                                <section key={key} className="rounded-xl border border-slate-200 bg-white">
                                    <header className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100">
                                        <button type="button" className="text-left font-semibold text-slate-900"
                                            onClick={() => setOpen((o) => ({ ...o, [key]: !isOpen }))}>
                                            {isOpen ? "▾" : "▸"} {row.group?.name || "Без таблицы"}
                                        </button>
                                        {row.month.status === "closed" && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">закрыт</span>}
                                        <span className="text-sm text-slate-500">план {money(row.summary.kpi_plan)}</span>
                                        <span className="text-sm text-slate-500">расчёт {money(row.summary.kpi_calc)}</span>
                                        <span className={`text-sm ${deltaClass(row.summary.kpi_delta)}`}>дельта {money(row.summary.kpi_delta)}</span>
                                        <span className="text-sm font-semibold text-slate-800">к оплате {money(row.summary.total_pay)}</span>
                                        <div className="flex-1" />
                                        <Link to={`/kpi/months/${row.month.id}`} className={btnGhost}>Открыть</Link>
                                    </header>
                                    {isOpen && (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                                <thead className="text-xs text-slate-500">
                                                    <tr className="text-left">
                                                        <th className="px-4 py-2 font-medium">Карта</th>
                                                        <th className="px-2 py-2 font-medium">Грейд</th>
                                                        <th className="px-2 py-2 font-medium text-right">Оклад</th>
                                                        <th className="px-2 py-2 font-medium text-right">Коэф. раб. вр.</th>
                                                        <th className="px-2 py-2 font-medium text-right">Выполнение</th>
                                                        <th className="px-2 py-2 font-medium text-right">План</th>
                                                        <th className="px-2 py-2 font-medium text-right">Расчёт</th>
                                                        <th className="px-2 py-2 font-medium text-right">Дельта</th>
                                                        <th className="px-2 py-2 font-medium text-right">Доп. задачи</th>
                                                        <th className="px-4 py-2 font-medium text-right">К оплате</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {row.cards.map((c) => (
                                                        <tr key={c.id} className="border-t border-slate-100 hover:bg-amber-50/40 cursor-pointer"
                                                            onClick={() => navigate(`/kpi/months/${row.month.id}?card=${c.id}`)}>
                                                            <td className="px-4 py-2">
                                                                <p className="font-medium text-slate-800">{c.employee_name || c.title}</p>
                                                                {c.employee_name && c.title !== c.employee_name && <p className="text-xs text-slate-500">{c.title}</p>}
                                                            </td>
                                                            <td className="px-2 py-2">{c.grade || "—"}</td>
                                                            <td className="px-2 py-2 text-right">{money(c.salary)}</td>
                                                            <td className="px-2 py-2 text-right">{percent(c.work_coef)}</td>
                                                            <td className="px-2 py-2 text-right">{percent(c.points_done_pct)}</td>
                                                            <td className="px-2 py-2 text-right">{money(c.kpi_plan)}</td>
                                                            <td className="px-2 py-2 text-right">{money(c.kpi_calc)}</td>
                                                            <td className={`px-2 py-2 text-right ${deltaClass(c.kpi_delta)}`}>{money(c.kpi_delta)}</td>
                                                            <td className="px-2 py-2 text-right">{money(c.extra_tasks_paid)}</td>
                                                            <td className="px-4 py-2 text-right font-semibold">{money(c.total_pay)}</td>
                                                        </tr>
                                                    ))}
                                                    {!row.cards.length && (
                                                        <tr><td colSpan={10} className="px-4 py-3 text-slate-500">Карт нет</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </section>
                            );
                        })}
                    </>
                )}
            </div>
        </MainLayout>
    );
}
